import { ArrowDown, ArrowRight } from 'lucide-react';
import { useSite } from '../context/SiteContext';
import { pathFor } from '../i18n/routes';
import useSEO from '../hooks/useSEO';
import { IMG, VIDEO, VENUE_IMAGES } from '../data/media';
import { MaskedLines, Kicker, Reveal } from '../components/Reveal';
import HoverRevealList from '../components/HoverRevealList';
import { CTABanner } from '../components/WorkGrid';

export default function Souvenir() {
  const { lang, t } = useSite();
  useSEO(t.meta.souvenir, 'souvenir', lang);

  const venues = t.souvenir.venues.map((v, i) => ({
    title: v,
    img: VENUE_IMAGES[i],
    badge: i === 0 ? t.souvenir.venueActive : null,
  }));
  const coversImgs = ['/assets/foto-karting.webp', '/assets/capa-karting.webp', '/assets/souvenir-covers-03.webp'];
  const coversPos = ['72% center', 'center', 'center'];

  return (
    <div data-testid="souvenir-page">
      <section className="relative flex min-h-[92svh] items-end overflow-hidden">
        <video
          autoPlay
          muted
          loop
          playsInline
          poster="/assets/souvenir-track-2.webp"
          className="absolute inset-0 h-full w-full object-cover opacity-40"
          data-testid="souvenir-hero-video"
        >
          <source src={VIDEO.souvenir} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/60" />
        <div className="absolute -left-32 top-1/4 h-[420px] w-[420px] rounded-full bg-fl-blue/15 blur-[130px]" aria-hidden="true" />
        <div className="relative z-10 mx-auto w-full max-w-[1600px] px-5 pb-20 pt-40 sm:px-8">
          <Kicker className="!text-fl-blue">F&L Souvenir</Kicker>
          <h1 className="mt-6 max-w-4xl font-heading text-5xl font-black uppercase leading-[0.95] tracking-tight sm:text-7xl">
            <MaskedLines delay={0.2} lines={[t.souvenir.title]} />
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-6 max-w-xl text-base text-zinc-400 sm:text-lg">{t.souvenir.desc}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-24 sm:py-32" data-testid="souvenir-steps">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8">
          <Reveal className="mb-14">
            <Kicker>{t.souvenir.stepsKicker}</Kicker>
            <h2 className="mt-4 font-heading text-4xl font-extrabold uppercase tracking-tight sm:text-5xl">
              {t.souvenir.stepsTitle}
            </h2>
          </Reveal>
          <div className="grid gap-px bg-zinc-900 sm:grid-cols-2 lg:grid-cols-4">
            {t.souvenir.steps.map((step, i) => (
              <Reveal key={step.title} delay={i * 0.12} className="bg-black">
                <div className="group flex h-full flex-col p-8 sm:p-10" data-testid={`souvenir-step-${i}`}>
                  <span className="font-heading text-6xl font-black text-stroke transition-colors duration-500 group-hover:text-fl-blue group-hover:[-webkit-text-stroke:0px]">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 className="mt-8 font-heading text-xl font-bold uppercase tracking-wide brand-text">
                    {step.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-500">{step.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-zinc-900 py-24 sm:py-32" data-testid="souvenir-venues">
        <div className="mx-auto mb-14 max-w-[1600px] px-5 sm:px-8">
          <Reveal>
            <Kicker>{t.souvenir.venuesKicker}</Kicker>
            <h2 className="mt-4 font-heading text-4xl font-extrabold uppercase tracking-tight sm:text-5xl">
              {t.souvenir.venuesTitle}
            </h2>
            <p className="mt-4 max-w-xl text-sm text-zinc-500">{t.souvenir.venueNote}</p>
          </Reveal>
        </div>
        <HoverRevealList items={venues} testIdPrefix="venue" />
      </section>

      <section className="relative overflow-hidden border-t border-zinc-900 py-24 sm:py-32" data-testid="souvenir-covers">
        <img src={IMG.textureMetallic} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-15" />
        <div className="relative z-10 mx-auto max-w-[1600px] px-5 sm:px-8">
          <Reveal>
            <Kicker>{t.souvenir.coversKicker}</Kicker>
            <h2 className="mt-4 max-w-3xl font-heading text-4xl font-extrabold uppercase tracking-tight sm:text-5xl">
              {t.souvenir.coversTitle}
            </h2>
            <p className="mt-6 max-w-2xl leading-relaxed text-zinc-400">{t.souvenir.coversText}</p>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {t.souvenir.coversFlow.map((label, i) => (
              <Reveal key={label} delay={i * 0.15}>
                <div className="group relative" data-testid={`covers-step-${i}`}>
                  <div className="image-frame aspect-[3/4]">
                    <img
                      src={coversImgs[i]}
                      alt={label}
                      loading="lazy"
                      style={{ objectPosition: coversPos[i] }}
                      className="transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                    />
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <p className="font-heading text-lg font-bold uppercase tracking-wide">
                      <span className="mr-3 font-mono text-xs brand-text">{String(i + 1).padStart(2, '0')}</span>
                      {label}
                    </p>
                    {i < 2 && (
                      <>
                        <ArrowRight className="hidden h-5 w-5 text-zinc-600 md:block" />
                        <ArrowDown className="h-5 w-5 text-zinc-600 md:hidden" />
                      </>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-zinc-900 py-24 sm:py-32" data-testid="souvenir-b2b">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8">
          <Reveal className="mb-14">
            <Kicker>{t.souvenir.b2bKicker}</Kicker>
            <h2 className="mt-4 max-w-3xl font-heading text-4xl font-extrabold uppercase tracking-tight sm:text-5xl">
              {t.souvenir.b2bTitle}
            </h2>
          </Reveal>
          <div className="grid gap-px bg-zinc-900 sm:grid-cols-2 lg:grid-cols-3">
            {t.souvenir.benefits.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.08} className="bg-black">
                <div className="group h-full p-8 transition-colors duration-500 hover:bg-zinc-950 sm:p-10" data-testid={`benefit-${i}`}>
                  <span className="font-mono text-xs text-zinc-600 transition-colors duration-300 group-hover:text-fl-blue">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-bold uppercase tracking-wide text-white">
                    {b.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-500">{b.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABanner
        title={t.souvenir.ctaTitle}
        text={t.souvenir.ctaText}
        cta={t.souvenir.ctaButton}
        email="souvenir@flgroup.pt"
        to={pathFor('contact', lang)}
        testId="souvenir-cta"
      />
    </div>
  );
}
